'use client';

import React, { useState, useEffect, useRef } from 'react';

// components
import { PHONE, EMAIL, DEFAULT_COUNTRY_CODE, OTP_LENGTH } from '@/constants';
import { useDispatch, useSelector } from 'react-redux';
import {
  setContactUpdateErrorMessage,
  setIsAccessDenied,
  setOtpVerificationLocked,
} from '@/redux/reducers/my-profile-reducer';
import { getOtp } from '@/redux/thunk/my-profile-thunk';
import { cn } from '@/utils/cn';
import Button from '../Button';
import Text from '../Text';
import Icon from '../Icon';

// utils
import ErrorMessage from './ErrorMessage';
import PhoneNumberInput from './PhoneInput';
import EmailInput from './EmailInput';
import MyProfileHeader from './MyProfileHeader';

function MyProfileInput({ activeTab, onUserInput, userInfo }) {
  // select the correct state based on the active tab

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isInvalidPhone, setIsInvalidPhone] = useState(true);
  const [isInvalidEmail, setIsInvalidEmail] = useState(true);
  const [isOpenCountrySelect, setIsOpenCountrySelect] = useState(false);
  const [isInputBoxClicked, setIsInputBoxClicked] = useState(false);
  const dispatch = useDispatch();

  const inputRef = useRef(null);
  const clearingRef = useRef(false);

  const {
    loading,
    contactUpdateErrorMessage,
    currentPage,
    otpVerificationLocked,
  } = useSelector((state) => state.myProfile);

  const handleClearMouseDown = () => {
    setIsInvalidPhone(true);
    setIsInvalidEmail(true);
    clearingRef.current = true;
  };

  const handleClearMouseUp = () => {
    setEmail('');
    setPhone('');
    dispatch(setIsAccessDenied(false));
    dispatch(setOtpVerificationLocked(false));
    dispatch(setContactUpdateErrorMessage(''));
    if (inputRef.current) {
      clearingRef.current = false;
      inputRef.current.focus();
    }
  };

  const resetField = () => {
    setEmail('');
    setPhone('');
  };

  const handleOtpSent = async () => {
    if (activeTab === PHONE) {
      await dispatch(
        getOtp({
          body: {
            purpose: 'contact-update',
            validity: 300,
            length: OTP_LENGTH,
            numericOnly: true,
            recipients: [
              {
                channel: 'sms',
                msisdn: `${DEFAULT_COUNTRY_CODE}${phone}`,
              },
            ],
          },
        }),
      );
    } else {
      await dispatch(
        getOtp({
          body: {
            purpose: 'contact-update',
            validity: 300,
            length: OTP_LENGTH,
            numericOnly: true,
            recipients: [
              {
                channel: 'email',
                email,
              },
            ],
          },
        }),
      );
    }
  };

  const onBlur = (doReturn = false) => {
    // write code if we want to add any onBlur functionality
    setIsInputBoxClicked(false);
  };

  const onEnterPress = ({ keyCode }) => {
    if (keyCode === 13 && !onBlur(true)) {
      if (isInvalidPhone) {
        return null;
      }
      if (isInvalidEmail) {
        return null;
      }
      handleOtpSent();
    }
  };

  useEffect(() => {
    if (activeTab === PHONE) {
      if (phone?.length >= 10) {
        setIsInvalidPhone(false);
        onUserInput(phone);
      } else {
        setIsInvalidPhone(true);
      }
    } else if (activeTab === EMAIL) {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (regex.test(email)) {
        setIsInvalidEmail(false);
        onUserInput(email);
      } else {
        setIsInvalidEmail(true);
      }
    }
  }, [phone, activeTab, onUserInput]);

  useEffect(() => {
    if (currentPage === 'form') resetField();
  }, [currentPage]);

  // console.log('>>>>>>activeTab', activeTab);

  return (
    <div className="my-profile-input bg-white rounded-xl flex flex-col items-center p-0 mt-4 lg:!mt-0 mx-0 lg:!mx-0 lg:p-24 ">
      <MyProfileHeader
        type="update-profile"
        userInfo={userInfo}
        activeTab={activeTab}
      />
      {activeTab === PHONE ? (
        <PhoneNumberInput
          isInputBoxClicked={isInputBoxClicked}
          setIsInputBoxClicked={setIsInputBoxClicked}
          selectedCountry={DEFAULT_COUNTRY_CODE}
          isOpenCountrySelect={isOpenCountrySelect}
          setIsOpenCountrySelect={setIsOpenCountrySelect}
          inputRef={inputRef}
          clearingRef={clearingRef}
          setPhone={setPhone}
          phone={phone}
          onEnterPress={onEnterPress}
          onBlur={onBlur}
          handleClearMouseDown={handleClearMouseDown}
          handleClearMouseUp={handleClearMouseUp}
        />
      ) : (
        <EmailInput
          isInputBoxClicked={isInputBoxClicked}
          setIsInputBoxClicked={setIsInputBoxClicked}
          inputRef={inputRef}
          clearingRef={clearingRef}
          onEnterPress={onEnterPress}
          onBlur={onBlur}
          handleClearMouseDown={handleClearMouseDown}
          handleClearMouseUp={handleClearMouseUp}
          email={email}
          setEmail={setEmail}
          setIsInvalidEmail={setIsInvalidEmail}
          onUserInput={onUserInput}
          activeTab={activeTab}
        />
      )}

      {contactUpdateErrorMessage ? (
        <ErrorMessage
          textPrimary={`Invalid ${activeTab === PHONE ? ' phone number' : ' email address'}!
          Please Try Again.`}
          type="textOnly"
        />
      ) : null}

      {/* For Desktop */}
      <Button
        type="primary"
        className={cn(
          'hidden lg:block min-w-[80px] bg-tealgreen text-white  py-12 px-10 rounded-[100px] gap-2 !text-16 w-full !font-bold border my-20 lg:!mt-24 lg:!mb-0',
          (loading ||
            otpVerificationLocked ||
            (activeTab === PHONE ? isInvalidPhone : isInvalidEmail)) &&
            '!bg-aquarelle-purple !text-atelier !border-himalayan-poppy',
        )}
        onClick={() => handleOtpSent()}
        disabled={
          loading ||
          otpVerificationLocked ||
          (activeTab === PHONE ? isInvalidPhone : isInvalidEmail)
        }
      >
        {loading && <Icon name="refresh" className="w-6 h-6 animate-spin" />}
        <Text
          type="span"
          className="text-14 lg:text-16 text-inherit font-semibold font-encode-sans leading-24 tracking-[0.32px]"
        >
          Save
        </Text>
      </Button>

      {/* For Mobile */}
      <Button
        type="primary"
        className={cn(
          'block lg:hidden min-w-[80px] bg-tealgreen text-white  py-12 px-10 rounded-[100px] gap-2 !text-16 w-full !font-bold border my-20 lg:!mt-24 lg:!mb-0',
          (loading ||
            otpVerificationLocked ||
            (activeTab === PHONE ? isInvalidPhone : isInvalidEmail)) &&
            '!bg-aquarelle-purple !text-atelier !border-himalayan-poppy',
        )}
        onClick={() => handleOtpSent()}
        disabled={
          loading ||
          otpVerificationLocked ||
          (activeTab === PHONE ? isInvalidPhone : isInvalidEmail)
        }
      >
        {loading && <Icon name="refresh" className="w-6 h-6 animate-spin" />}
        <Text
          type="span"
          className="text-14 lg:text-16 text-inherit font-semibold font-encode-sans leading-24 tracking-[0.32px]"
        >
          Save
        </Text>
      </Button>
    </div>
  );
}

export default MyProfileInput;
